import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { DATE_FORMAT, DATETIME_FORMAT, TIME_FORMAT } from './constants'

// Enable UTC and timezone plugins
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Parse UTC time string and convert to local timezone
 * Backend returns UTC time, we need to display in user's local timezone
 */
const parseUTCToLocal = (utcTimeString) => {
  if (!utcTimeString) return null
  // Parse as UTC and convert to local timezone
  return dayjs.utc(utcTimeString).local()
}

/**
 * Parse New York time string and convert to local timezone
 * Scheduled task timestamps are stored in America/New_York
 */
export const parseNyToLocal = (nyTimeString) => {
  if (!nyTimeString) return null
  return dayjs.tz(nyTimeString, 'America/New_York').local()
}

/**
 * Format date to string (with UTC to local conversion)
 */
export const formatDate = (date, format = DATE_FORMAT) => {
  if (!date) return ''
  const localDate = parseUTCToLocal(date)
  return localDate ? localDate.format(format) : ''
}

/**
 * Format datetime to string (with UTC to local conversion)
 */
export const formatDateTime = (date) => {
  return formatDate(date, DATETIME_FORMAT)
}

/**
 * Format time to string (with UTC to local conversion)
 */
export const formatTime = (date) => {
  return formatDate(date, TIME_FORMAT)
}

/**
 * Get relative time (e.g., "2 hours ago") (with UTC to local conversion)
 */
export const getRelativeTime = (date) => {
  if (!date) return ''
  const now = dayjs()
  const target = parseUTCToLocal(date)
  if (!target) return ''

  const diffMs = now.diff(target)
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`
  return formatDate(date)
}

/**
 * Format a date range in compact US style.
 * Same year:      "Mar 14 – Apr 14, 2026"
 * Different year: "Dec 15, 2025 – Jan 15, 2026"
 * Accepts ISO date strings (YYYY-MM-DD) or dayjs-parseable values.
 */
export const formatDateRange = (start, end) => {
  if (!start || !end) return ''
  const s = dayjs(start)
  const e = dayjs(end)
  if (!s.isValid() || !e.isValid()) return `${start} – ${end}`

  if (s.year() === e.year()) {
    return `${s.format('MMM D')} – ${e.format('MMM D')}, ${e.format('YYYY')}`
  }
  return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`
}

/**
 * Truncate text with ellipsis
 */
export const truncate = (text, maxLength = 50) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Get status color for Ant Design
 */
export const getStatusColor = (status) => {
  const colors = {
    pending: 'default',
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'warning',
  }
  return colors[status?.toLowerCase()] || 'default'
}

/**
 * Get decision color class
 */
export const getDecisionColor = (decision) => {
  if (!decision) return 'default'
  const lowerDecision = decision.toLowerCase()
  if (lowerDecision.includes('buy')) return 'success'
  if (lowerDecision.includes('sell')) return 'error'
  return 'warning'
}

/**
 * Get decision badge type for Ant Design
 */
export const getDecisionBadgeStatus = (decision) => {
  if (!decision) return 'default'
  const lowerDecision = decision.toLowerCase()
  if (lowerDecision.includes('buy')) return 'success'
  if (lowerDecision.includes('sell')) return 'error'
  return 'warning'
}

/**
 * Parse WebSocket URL with task ID
 */
export const parseWsUrl = (template, taskId) => {
  return template.replace(':taskId', taskId)
}

/**
 * Parse API URL with parameters
 */
export const parseApiUrl = (template, params = {}) => {
  let url = template
  Object.keys(params).forEach((key) => {
    url = url.replace(`:${key}`, params[key])
  })
  return url
}

/**
 * Deep clone object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Check if value is empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

/**
 * Debounce function
 */
export const debounce = (func, wait = 300) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Throttle function
 */
export const throttle = (func, limit = 300) => {
  let inThrottle
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

/**
 * Sleep for specified milliseconds
 */
export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Format number with commas
 */
export const formatNumber = (num) => {
  if (!num && num !== 0) return '0'
  return num.toLocaleString()
}

/**
 * Format percentage
 */
export const formatPercentage = (value, decimals = 2) => {
  if (!value && value !== 0) return '0%'
  return `${value.toFixed(decimals)}%`
}

/**
 * Get the scheduled task ID attached to a task response.
 */
export const getScheduledTaskId = (task) => {
  return task?.scheduledTaskId ?? task?.scheduled_task_id ?? null
}

/**
 * Check whether a task execution was created by a subscription.
 */
export const isSubscriptionGeneratedTask = (task) => {
  const scheduledTaskId = getScheduledTaskId(task)
  return scheduledTaskId !== null && scheduledTaskId !== undefined
}

/**
 * Safely parse JSON
 */
export const safeJsonParse = (str, defaultValue = null) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    return defaultValue
  }
}

/**
 * Get message type icon
 */
export const getMessageTypeIcon = (type) => {
  const icons = {
    System: '📊',
    Reasoning: '🧠',
    tool_call: '🔧',
    Analysis: '📈',
  }
  return icons[type] || '📝'
}

/**
 * Get message type label
 */
export const getMessageTypeLabel = (type) => {
  if (!type) return 'UNKNOWN'
  const labels = {
    System: 'SYSTEM',
    Reasoning: 'REASONING',
    tool_call: 'TOOL CALL',
    Analysis: 'ANALYSIS',
  }
  return labels[type] || type.toUpperCase()
}

/**
 * Calculate task duration (with UTC to local conversion)
 */
export const calculateDuration = (startTime, endTime) => {
  if (!startTime) return 'N/A'
  const start = parseUTCToLocal(startTime)
  const end = endTime ? parseUTCToLocal(endTime) : dayjs()
  if (!start) return 'N/A'

  const diffMs = end.diff(start)

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  }
  return `${seconds}s`
}

/**
 * Validate ticker symbol
 */
export const isValidTicker = (ticker) => {
  if (!ticker) return false
  // Basic validation: 1-5 uppercase letters
  return /^[A-Z]{1,5}$/.test(ticker.trim())
}

/**
 * Get agent display name
 */
export const getAgentDisplayName = (agentId) => {
  const nameMap = {
    'market': 'Market Analyst',
    'social': 'Social Analyst',
    'news': 'News Analyst',
    'fundamentals': 'Fundamentals Analyst',
    'bull': 'Bull Researcher',
    'bear': 'Bear Researcher',
    'research-manager': 'Research Manager',
    'trader': 'Trader',
    'risk-risky': 'Aggressive Analyst',
    'risk-safe': 'Conservative Analyst',
    'risk-neutral': 'Neutral Analyst',
    'risk-manager': 'Risk Manager',
  }
  return nameMap[agentId] || agentId
}

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    console.error('Failed to copy:', err)
    return false
  }
}

/**
 * Download text as file
 */
export const downloadAsFile = (content, filename, mimeType = 'text/plain') => {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Format currency amount
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) {
    return null
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch (error) {
    console.warn('Failed to format currency:', error)
    return `${amount} ${currency || 'USD'}`
  }
}
